import { useItineraryContext } from "modules/PortPredictor/hooks/itineraryContext"
import {
  PortPredictorCargoGroupTypes,
  PortPredictorCargoesData,
} from "modules/PortPredictor/hooks/usePortPredictorAPI"
import { Form, OverlayTrigger, Popover, Spinner } from "react-bootstrap"
import Select, { SingleValue } from "react-select"
import { formatCargoLabel } from "../portPredictorFormatCargoLabelHelper"

import { useEffect, useState } from "react"
import { PortPredictorChosenCargo } from "modules/PortPredictor/portPredictorReducer"

interface PortPredictorCargoInputsProps {
  cargoesData: PortPredictorCargoesData[] | undefined
  isCargoesLoading: boolean
  isCargoesError: boolean
  chosenCargo: PortPredictorChosenCargo
  setChosenCargo: (cargo: PortPredictorChosenCargo) => void
  isDisabled: boolean
}

function getDropdownPadding(cargoGroup: PortPredictorCargoGroupTypes) {
  if (cargoGroup === PortPredictorCargoGroupTypes.MajorGroup) {
    return "1rem"
  }
  if (cargoGroup === PortPredictorCargoGroupTypes.MinorGroup) {
    return "2rem"
  }
  if (cargoGroup === PortPredictorCargoGroupTypes.Commodity) {
    return "3rem"
  }
}

const customStyles = {
  option: (provided: any, state: any) => ({
    ...provided,
    fontWeight: state.data.cargoGroup === "major_group" ? "bold" : "normal",
    paddingLeft: getDropdownPadding(state.data.cargoGroup),
  }),
}

function formatChosenCargoLabel(chosenCargo: PortPredictorChosenCargo) {
  if (!chosenCargo.cargo) {
    return ""
  }
  const formatted = formatCargoLabel(chosenCargo.cargo)
  if (chosenCargo.cargoGroup === PortPredictorCargoGroupTypes.MajorGroup) {
    return formatted
  }
  if (chosenCargo.cargoGroup === PortPredictorCargoGroupTypes.MinorGroup) {
    return `${formatted} (minor group)`
  }
  return `${formatted} (commodity)`
}

export function PortPredictorCargoInputs({
  cargoesData,
  isCargoesLoading,
  isCargoesError,
  chosenCargo,
  setChosenCargo,
  isDisabled,
}: PortPredictorCargoInputsProps) {
  const { getTabStateKeyValue } = useItineraryContext()
  const chosenCargoLabel = formatChosenCargoLabel(chosenCargo)

  const [cargoOption, setCargoOption] = useState<{
    label: string
    value: string
    cargoValue?: string
    cargoGroup?: PortPredictorCargoGroupTypes
  }>({
    label: chosenCargoLabel,
    value:
      chosenCargo.cargoGroup === PortPredictorCargoGroupTypes.MajorGroup
        ? chosenCargo.cargo
          ? chosenCargo.cargo
          : ""
        : `${chosenCargo.cargo}-${chosenCargo.cargoGroup}`,
    cargoValue: chosenCargo.cargo,
    cargoGroup: chosenCargo.cargoGroup,
  })

  useEffect(() => {
    if (!cargoOption?.cargoValue || !cargoOption?.cargoGroup) {
      return
    }
    setChosenCargo({
      cargo: cargoOption.cargoValue,
      cargoGroup: cargoOption.cargoGroup,
    })
  }, [cargoOption?.cargoGroup, cargoOption?.cargoValue])

  return (
    <div className="d-flex align-items-end flex-column ">
      <div className="d-flex w-100 align-items-center">
        <div className="PortPredictor_InputWrapper w-100">
          <Form.Label className="PortPredictor_FormLabel fw-bold">
            <>Most Likely Cargo</>
          </Form.Label>
          <Select
            isDisabled={isDisabled || !getTabStateKeyValue("isEditMode")}
            placeholder={
              isCargoesLoading && cargoesData === undefined ? (
                <div>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    variant="light"
                  />
                  <span className="ms-1">Loading Cargoes...</span>
                </div>
              ) : (
                "Select Cargo"
              )
            }
            className="PortPredictor_InputGroup"
            styles={customStyles}
            options={cargoesData ? transformCargoGroups(cargoesData) : []}
            value={
              cargoOption?.value && cargoOption?.cargoGroup
                ? {
                    label: cargoOption.label,
                    value: cargoOption.value,
                    cargoValue: cargoOption.cargoValue,
                    cargoGroup: cargoOption.cargoGroup,
                  }
                : undefined
            }
            onChange={(
              cargo: SingleValue<{
                label: string
                value: string
                cargoValue?: string
                cargoGroup: PortPredictorCargoGroupTypes
              }>
            ) => {
              if (!cargo) {
                return
              }
              setCargoOption(cargo)
            }}
          />
        </div>
        <div className="PortPredictor_InputAlert">
          <OverlayTrigger placement="bottom" overlay={renderCargoDisclaimer()}>
            <i className="ti-info-alt ms-1 d-flex align-items-center" />
          </OverlayTrigger>
        </div>
      </div>
      <div className="fs-sm fc-gray">
        Select dropdown for more cargo options
      </div>

      {isCargoesError && <div>Unable to find cargoes for selected port</div>}
    </div>
  )
}

function renderCargoDisclaimer() {
  return (
    <Popover id="cargo-disclaimer">
      <Popover.Body>
        <div>
          <span style={{ fontWeight: "bold" }}>Major Group</span> - Broad
          category, includes multiple types of cargo within the group.
        </div>
        <div>
          <span style={{ fontWeight: "bold" }}>Minor Group</span> - Intermediate
          level, includes subsets within the sub-group.
        </div>
        <div>
          <span style={{ fontWeight: "bold" }}>Commodity</span> - Most specific
          level, focuses on a single item
        </div>
      </Popover.Body>
    </Popover>
  )
}

function transformCargoGroups(cargoesData: PortPredictorCargoesData[]) {
  const map = new Map()
  const majorOrder: string[] = []

  // Group by major_group -> minor_group -> commodities
  cargoesData.forEach(({ major_group, minor_group, commodity }) => {
    if (!map.has(major_group)) {
      map.set(major_group, new Map())
      majorOrder.push(major_group)
    }
    const minorMap = map.get(major_group)
    if (!minorMap.has(minor_group)) {
      minorMap.set(minor_group, [])
    }
    minorMap.get(minor_group).push(commodity)
  })

  // Build the result array in order of first appearance
  const result = [] as {
    label: string
    value: string
    cargoValue: string
    cargoGroup: PortPredictorCargoGroupTypes
  }[]
  majorOrder.forEach((major) => {
    const formatted = formatCargoLabel(major)
    result.push({
      label: formatted,
      value: major,
      cargoValue: major,
      cargoGroup: PortPredictorCargoGroupTypes.MajorGroup,
    })
    const minorMap = map.get(major)
    minorMap.forEach((commodities: string[], minor: string) => {
      const formatted = formatCargoLabel(minor)
      result.push({
        label: `${formatted} (minor group)`,
        value: `${minor}-minor_group`,
        cargoValue: minor,
        cargoGroup: PortPredictorCargoGroupTypes.MinorGroup,
      })
      commodities.forEach((c) => {
        const formatted = formatCargoLabel(c)
        result.push({
          label: `${formatted + " (commodity)"}`,
          value: `${c}-commodity`,
          cargoValue: c,
          cargoGroup: PortPredictorCargoGroupTypes.Commodity,
        })
      })
    })
  })

  return result
}
